// Thin client for the server's upload API (server/src/http/uploads.ts).
//
// The client never branches on transport: initiate answers a part plan plus
// URLs, and parts are PUT wherever those URLs point (same-origin routes in
// local mode, presigned S3 in cloud). Part→URL association is a Map keyed by
// partNumber, never an index-rebuilt array — rebuilt arrays under concurrency
// are exactly the corruption suspect called out in the sync worker's config.

import { getConfig } from '../../config/runtimeConfig';
import { noteUnauthorized } from '../../auth/sessionWatch';
import type { ImageKind, ImageType, Laterality } from '../../data/imageTypes';

export interface PartUrl {
  partNumber: number;
  url: string;
}

export interface InitiateResponse {
  uploadId: string;
  name: string;
  partSize: number;
  partCount: number;
  partConcurrency: number;
  urlTtlSeconds: number;
  /** How often to say the transfer is alive (see touchUpload); absent on an older server. */
  heartbeatMs?: number;
  urls: PartUrl[];
}

export interface UploadsConfig {
  extensions: string[];
  partConcurrency: number;
  maxFileSizeBytes: number;
  /**
   * Where uploads may go, default first.
   *
   * Empty where there is nothing to choose — a deployment with no auth has one
   * implicit destination — so the page can simply not draw a picker.
   */
  workspaces?: UploadWorkspace[];
}

export class UploadApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly reason?: string,
    /** The local transport's "parts missing" answer names the parts. */
    readonly missing?: number[],
    /** Everything else the answer carried (e.g. `mine`, `retryAfterMs` on a 409). */
    readonly body?: Record<string, unknown>,
  ) {
    super(message);
  }
}

function root(): string {
  return getConfig().apiRoot;
}

/**
 * Whether to send the session cookie.
 *
 * NONE OF THESE CALLS SENT IT, which is the bug this fixes. The page is served
 * from one origin and the API answers on another (:3004 and :8043 in
 * development, and a different host in cloud), so fetch's default of
 * `same-origin` silently omitted the cookie on every request in this file —
 * while the workspaces client, which sets it explicitly, worked fine beside it.
 * The upload page therefore 401'd on a session every other page accepted, and
 * reported it as uploads failing.
 *
 * CONDITIONAL, not always 'include'. `Access-Control-Allow-Origin: *` and
 * credentialed requests are mutually exclusive by specification, and the
 * default local stack runs AUTH_MODE=off with exactly that wildcard — asking
 * for credentials there makes the browser reject a response the server was
 * happy to give. Deployments with authentication configured send a specific
 * origin and can take the cookie.
 */
function creds(): RequestCredentials {
  return getConfig().auth ? 'include' : 'same-origin';
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let message = `${res.status}`;
    let reason: string | undefined;
    let missing: number[] | undefined;
    let raw: Record<string, unknown> | undefined;
    try {
      const body = (await res.json()) as {
        error?: string;
        reason?: string;
        missing?: unknown;
      } & Record<string, unknown>;
      raw = body;
      if (body.error) message = body.error;
      reason = body.reason;
      if (Array.isArray(body.missing)) missing = body.missing.filter((n): n is number => Number.isInteger(n) && (n as number) >= 1);
    } catch {
      /* non-JSON error body */
    }
    throw new UploadApiError(message, res.status, reason, missing, raw);
  }
  return (await res.json()) as T;
}

/** A workspace this account may upload into. */
export interface UploadWorkspace {
  public_id: string;
  name: string;
  is_default: boolean;
}

export async function fetchUploadsConfig(): Promise<UploadsConfig> {
  return json(await fetch(`${root()}/uploads/config`, { credentials: creds() }));
}

// ── drafts (server/src/http/drafts.ts) ──────────────────────────────────────
// Every web upload is a draft: its image (and, in community, its study) is a
// row before a byte is sent, details go straight to the catalogue, and the
// image's bytes are sent to the image (initiateImageUpload) rather than under
// a file name. Community makes the study first (createDraftStudy); enterprise
// makes the image alone and names its case by accession (createImage).

/** A coded detail: an anatomic site or a stain (SNOMED CT). */
export interface CodedValue {
  code: string;
  scheme: string;
  meaning: string;
}

/** Where a draft image is (drafts.ts viewOf). */
export type ImageStage = 'waiting-file' | 'uploading' | 'converting' | 'ingesting' | 'needs-details' | 'published' | 'failed';

/**
 * An image's details as the server takes them: null clears, absent leaves
 * alone. The Type (`image_type`) is what the forms ask; the server writes the
 * modality from it. A raw `modality` is sent only when a file said one (a
 * parsed DICOM header, a metadata sidecar); the community forms never send it.
 * Nor does any form ask an `objective_power`: ingest reads it from the file,
 * and only a metadata sidecar sends one. A laterality is one of the three CID
 * 244 letters; the server writes the code.
 */
export interface ImageDetails {
  image_type?: ImageType | null;
  modality?: string | null;
  description?: string | null;
  specimen_short_description?: string | null;
  site?: CodedValue | null;
  laterality?: Laterality | null;
  stain?: CodedValue | null;
  objective_power?: number | null;
}

export interface DraftImage {
  image_id: string;
  /** The file name it was made with; for a slide of several DICOM files, the slide's name. */
  name: string | null;
  kind: string | null;
  /** 'tar' for a slide whose several DICOM files the page packed into one .tar (its files are selected again together). */
  packed?: 'tar' | null;
  /** Bytes of its latest upload; null before one started. */
  size: number | null;
  /** The Type; null for a DICOM arrival of a modality that fits none ("Other"). Absent from a server before 0088. */
  image_type?: ImageType | null;
  modality: string | null;
  description: string | null;
  specimen_short_description: string | null;
  site: CodedValue | null;
  /** CID 244 (SNOMED CT), shaped like the site; never without one. Absent from a server before 0088. */
  laterality?: CodedValue | null;
  stain: CodedValue | null;
  objective_power: number | null;
  stage: ImageStage;
  /** 0 to 100 while converting or ingesting. */
  percent?: number;
  error: string | null;
  /** The server refused its upload for want of storage, and none has started since. */
  refused?: 'storage' | null;
  missing: Array<'modality' | 'site'>;
  created_utc: string;
  expires_utc: string | null;
  /** The upload to resume: only for the person who started it, while it is live. */
  upload: { id: string; partSize: number; partCount: number } | null;
}

/**
 * An image as the enterprise routes answer it: the draft, and what decides
 * whether it is shown — its details confirmed, the case it is in, and the
 * metadata the server is still waiting for.
 */
export interface EnterpriseImage extends DraftImage {
  /** Its details are confirmed (by a person, or resolved from the LIS). */
  ready: boolean;
  /** The study (case) it is in, summarised. */
  study: ImageStudy;
  /** Non-null while the server waits for its metadata (the LIS or a companion file). */
  metadata: { status: 'waiting'; overdue: boolean; error: string | null } | null;
  /** Everything else written about it, so a form reopened later shows it all; null only when the server cannot build it. */
  details: WrittenDetails | null;
}

/** The details of an enterprise image beyond its summary, as they are stored. */
export interface WrittenDetails {
  study: {
    sex: 'M' | 'F' | 'O' | null;
    /** DICOM AS, e.g. `054Y`. */
    age: string | null;
    diagnosis: string | null;
    /** `YYYY-MM-DD`. */
    study_date: string | null;
    /** DICOM TM, `HHMMSS`. */
    study_time: string | null;
    /** DICOM PN, `Family^Given`. */
    referring_physician: string | null;
    accession_issuer: string | null;
    patient_issuer: string | null;
    /** `YYYY-MM-DD`. */
    patient_birth_date: string | null;
  };
  image: {
    illumination: CodedValue | null;
    specimen_type: CodedValue | null;
    procedure: CodedValue | null;
    container_identifier: string | null;
    container_issuer: string | null;
    specimen_identifier: string | null;
    specimen_issuer: string | null;
    manufacturer: string | null;
    station_name: string | null;
    device_serial: string | null;
  };
}

/** The study an enterprise image is in, summarised. `patient_name` is DICOM PN (`Family^Given`). */
export interface ImageStudy {
  public_id: string;
  study_id: string | null;
  description: string | null;
  accession_number: string | null;
  patient_id: string | null;
  patient_name: string | null;
}

/** A study's details as the server takes them. Age in years, or "90+". */
export interface StudyDetails {
  study_id?: string;
  description?: string | null;
  diagnosis?: string | null;
  sex?: 'M' | 'F' | 'O' | null;
  age?: number | string | null;
  /** `YYYY-MM-DD` (a date the study already has; both editions keep it). Null clears. */
  study_datetime?: string | null;
}

/** Enterprise image details beyond the community five: coded acquisition and specimen facts, identifiers of the glass, the scanner. */
export interface EnterpriseImageDetails extends ImageDetails {
  illumination?: CodedValue | null;
  specimen_type?: CodedValue | null;
  procedure?: CodedValue | null;
  container_identifier?: string | null;
  container_issuer?: string | null;
  specimen_identifier?: string | null;
  specimen_issuer?: string | null;
  manufacturer?: string | null;
  station_name?: string | null;
  device_serial?: string | null;
}

/** A patient as the enterprise form names one. Sent whole: it replaces the name and birth date the patient had. */
export interface PatientDetails {
  id: string;
  issuer?: string | null;
  /** DICOM PN, `Family^Given`. */
  name?: string | null;
  /** `YYYY-MM-DD`. */
  birth_date?: string | null;
}

/**
 * An enterprise study's details (the case). A different accession number
 * moves the image into that case's study; null detaches it. The accession's
 * issuer is read only beside its number, so the two travel together.
 */
export interface CaseDetails extends Omit<StudyDetails, 'study_id'> {
  study_id?: string | null;
  accession_number?: string | null;
  accession_issuer?: string | null;
  /** DICOM PN, `Family^Given`. */
  referring_physician?: string | null;
  /** `YYYY-MM-DD` or `YYYY-MM-DDTHH:MM[:SS]`; without an offset the workspace's zone applies. */
  study_datetime?: string | null;
  patient?: PatientDetails | null;
}

const send = (method: string, body: unknown, signal?: AbortSignal): RequestInit => ({
  credentials: creds(),
  method,
  headers: { 'content-type': 'application/json' },
  body: JSON.stringify(body),
  signal,
});

/** A new study, filed into the collection (and group) named. 422 when free text looks like an identifier. */
export async function createDraftStudy(
  body: StudyDetails & { study_id: string; collectionId: string; studyGroupId?: string | null; request_key?: string },
  signal?: AbortSignal,
): Promise<{ public_id: string; study_instance_uid: string; workspace: string }> {
  return json(await fetch(`${root()}/studies`, send('POST', body, signal)));
}

/** A study's details: the community study fields, or an enterprise case's (a new accession renames the study's case, moving all its images). 409 when another study holds that accession. */
export async function patchStudyDetails(studyId: string, body: StudyDetails | CaseDetails, signal?: AbortSignal): Promise<void> {
  await json(await fetch(`${root()}/studies/${encodeURIComponent(studyId)}/details`, send('PATCH', body, signal)));
}

/** How the page packed what it sends: a tar of a slide's several files, named for the slide (tar.ts), and what those files are. */
export interface Packed {
  packed?: 'tar';
  /** The DICOM files of one series, or a MIRAX .mrxs with its data folder; the server takes the tar wherever it takes those. */
  holds?: 'dcm' | 'mrxs';
}

/**
 * A new image of the study, before its bytes: they go to initiateImageUpload
 * with its id. `kind` is what the page takes the file for (a slide or a flat
 * picture), so the server can check the Type fits it before the file is seen.
 */
export async function createDraftImage(studyId: string, body: ImageDetails & Packed & { name: string; kind?: ImageKind; request_key?: string }, signal?: AbortSignal): Promise<DraftImage> {
  return json(await fetch(`${root()}/studies/${encodeURIComponent(studyId)}/images`, send('POST', body, signal)));
}

export async function patchDraftImage(imageId: string, body: ImageDetails, signal?: AbortSignal): Promise<DraftImage> {
  return json(await fetch(`${root()}/images/${encodeURIComponent(imageId)}`, send('PATCH', body, signal)));
}

/** Every image of the study, and where each is. Members only. */
export async function fetchStudyImages(studyId: string, signal?: AbortSignal): Promise<DraftImage[]> {
  const r = await json<{ images: DraftImage[] }>(await fetch(`${root()}/studies/${encodeURIComponent(studyId)}/images`, { credentials: creds(), signal }));
  return r.images;
}

/** One of the person's uploads, as the top bar lists it (GET /uploads/active): a study, and the person's images in it since the upload began. */
export interface ActiveUpload {
  study: string;
  label: string | null;
  description: string | null;
  collection: string | null;
  collection_name: string | null;
  group: string | null;
  group_name: string | null;
  workspace: string | null;
  workspace_name: string | null;
  started_utc: string;
  /** Images with their bytes in that the server removed unpublished (a failed conversion, details never given). */
  not_imported: number;
  /** The workspace draws on the person's own organization's storage: their Usage page frees it. */
  yours: boolean;
  images: Array<{
    image_id: string;
    name: string | null;
    stage: ImageStage;
    percent: number | null;
    error: string | null;
    /** An upload another tab or device is sending right now (not one whose tab is gone). */
    live: boolean;
    /** Refused for want of storage, and not started since. */
    refused: 'storage' | null;
  }>;
}

/** The person's uploads under way, and those finished but not yet opened or dismissed; newest first. */
export async function fetchActiveUploads(signal?: AbortSignal): Promise<ActiveUpload[]> {
  const r = await json<{ studies: ActiveUpload[] }>(await fetch(`${root()}/uploads/active`, { credentials: creds(), signal }));
  return r.studies;
}

/** Take a finished upload off the list, everywhere. Already gone counts as done. */
export async function dismissActiveUpload(studyId: string): Promise<void> {
  const res = await fetch(`${root()}/uploads/active/${encodeURIComponent(studyId)}`, { credentials: creds(), method: 'DELETE' });
  if (res.ok || res.status === 404) return;
  await json(res);
}

/** Withdraw an unpublished image, with its upload and its bytes. Already gone counts as done. */
export async function deleteDraftImage(imageId: string): Promise<void> {
  const res = await fetch(`${root()}/images/${encodeURIComponent(imageId)}`, { credentials: creds(), method: 'DELETE' });
  if (res.ok || res.status === 404) return;
  await json(res);
}

// ── enterprise: slide by slide ──────────────────────────────────────────────

/**
 * A new image, before its bytes, in the case its accession names (or a study
 * of its own). The workspace is decided here, once: the image is a row in it.
 */
export async function createImage(
  body: EnterpriseImageDetails & Packed & { name: string; kind?: ImageKind; workspace?: string; request_key?: string; study?: CaseDetails },
  signal?: AbortSignal,
): Promise<EnterpriseImage> {
  return json(await fetch(`${root()}/images`, send('POST', body, signal)));
}

/** Its image details (PATCH /images/:id, the route community drafts use), answered as the enterprise page reads it. */
export async function patchImage(imageId: string, body: EnterpriseImageDetails, signal?: AbortSignal): Promise<EnterpriseImage> {
  return json(await fetch(`${root()}/images/${encodeURIComponent(imageId)}`, send('PATCH', body, signal)));
}

/** Its study's details; a new accession moves it to that case. 409 when a conflict prevents it. */
export async function patchImageStudy(imageId: string, body: CaseDetails, signal?: AbortSignal): Promise<EnterpriseImage> {
  return json(await fetch(`${root()}/images/${encodeURIComponent(imageId)}/study`, send('PATCH', body, signal)));
}

/** The person confirms its details: published once it is ingested. 400 `unidentified` without an accession or a patient. */
export async function confirmImage(imageId: string, signal?: AbortSignal): Promise<EnterpriseImage> {
  return json(await fetch(`${root()}/images/${encodeURIComponent(imageId)}/ready`, send('POST', {}, signal)));
}

/** One image, wherever its case put it; null when the server no longer has it (or it is not this person's to see). */
export async function fetchImage(imageId: string, signal?: AbortSignal): Promise<EnterpriseImage | null> {
  const res = await fetch(`${root()}/images/${encodeURIComponent(imageId)}`, { credentials: creds(), signal });
  if (res.status === 404) return null;
  return json(res);
}

/** This person's uploads to the workspace over the last `days` (images they made, sent, or whose study they own), newest first. */
export async function fetchMyImages(workspace: string | undefined, days = 7, signal?: AbortSignal): Promise<EnterpriseImage[]> {
  const q = new URLSearchParams({ days: String(days) });
  if (workspace) q.set('workspace', workspace);
  const r = await json<{ images: EnterpriseImage[] }>(await fetch(`${root()}/images?${q}`, { credentials: creds(), signal }));
  return r.images;
}

/**
 * Which of some files, taken in order, would fit in the image's organization
 * now, without starting their uploads (community). Yes or no per size: the
 * organization's figures are not told to someone uploading into another's workspace.
 */
export async function fetchUploadRoom(imageId: string, sizes: readonly number[], signal?: AbortSignal): Promise<boolean[]> {
  const q = new URLSearchParams({ bytes: sizes.join(',') });
  const r = await json<{ fits: boolean[] }>(await fetch(`${root()}/uploads/images/${encodeURIComponent(imageId)}/room?${q}`, { credentials: creds(), signal }));
  return r.fits;
}

/** Start sending an image's bytes. The parts, touch, complete and abort that follow are the ones every upload uses. */
export async function initiateImageUpload(imageId: string, body: { size: number; contentType?: string }, signal?: AbortSignal): Promise<InitiateResponse> {
  return json(await fetch(`${root()}/uploads/images/${encodeURIComponent(imageId)}`, send('POST', body, signal)));
}

/** More part URLs for an upload (the refresh path after presign expiry). */
export async function fetchPartUrls(
  uploadId: string,
  partNumbers: number[],
  signal?: AbortSignal,
): Promise<PartUrl[]> {
  const r = await json<{ urls: PartUrl[] }>(
    await fetch(`${root()}/uploads/parts-urls`, {
      credentials: creds(),
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ uploadId, partNumbers }),
      signal,
    }),
  );
  return r.urls;
}

/** Finish an upload: the server queues its image's conversion. */
export async function completeUpload(
  uploadId: string,
  parts: { partNumber: number; etag: string }[],
  signal?: AbortSignal,
): Promise<void> {
  await json(
    await fetch(`${root()}/uploads/${encodeURIComponent(uploadId)}/complete`, {
      credentials: creds(),
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ parts }),
      signal,
    }),
  );
}

export interface PartsAnswer {
  partCount: number;
  partSize: number;
  size: number;
  parts: { partNumber: number; etag: string; size: number }[];
  /** The complete already landed (its answer never reached the client): nothing left to send. */
  completed?: boolean;
}

/** The parts the server already holds for an upload (resume after a reload); null when it no longer knows the upload. */
export async function fetchParts(uploadId: string, signal?: AbortSignal): Promise<PartsAnswer | null> {
  const res = await fetch(`${root()}/uploads/${encodeURIComponent(uploadId)}/parts`, { credentials: creds(), signal });
  if (res.status === 404) return null;
  return json(res);
}

/** Says an upload is still being sent; without it a quiet one of the same person's is taken over as abandoned. */
export async function touchUpload(uploadId: string, signal?: AbortSignal): Promise<void> {
  await json(await fetch(`${root()}/uploads/${encodeURIComponent(uploadId)}/touch`, { credentials: creds(), method: 'POST', signal }));
}

/** Bytes in part `partNumber` of the plan initiate answered — the server's
 *  rule (server/src/http/uploads.ts expectedPartBytes), kept in step. */
export function expectedPartBytes(size: number, partSize: number, partNumber: number, partCount: number): number {
  return partNumber < partCount ? partSize : size - partSize * (partCount - 1);
}

export async function abortUpload(uploadId: string): Promise<void> {
  await fetch(`${root()}/uploads/${encodeURIComponent(uploadId)}/abort`, {
    credentials: creds(),
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: '{}',
  }).catch(() => {
    /* abort is best-effort */
  });
}

/**
 * PUT one part via XHR — fetch() still has no upload-progress events. The URL
 * may be same-origin (local) or presigned S3 (cloud); relative URLs are
 * resolved against apiRoot. Resolves with the part's ETag: S3 answers it
 * in the ETag header, the local route in the JSON body.
 */
export function putPart(
  url: string,
  bytes: Blob,
  opts: {
    onProgress?: (loadedBytes: number) => void;
    signal?: AbortSignal;
  } = {},
): Promise<{ etag: string }> {
  // A RELATIVE url is our own part route and needs the session cookie; an
  // ABSOLUTE one is presigned S3, where the signature is the credential and a
  // cookie would be both useless and a leak of it to a third party.
  // `//host/path` is NOT ours: it is protocol-relative and resolves off-origin,
  // but `!startsWith('http')` called it relative — which would have attached
  // credentials and prefixed it with apiRoot. Server-generated part URLs are
  // always either `/uploads/...` or an absolute presigned S3 URL, so this is
  // unreachable today; it is one character of slack in the test that decides
  // whether a cookie goes out.
  const ours = url.startsWith('/') && !url.startsWith('//');
  const absolute = ours ? `${root()}${url}` : url;
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', absolute);
    // Same conditional as creds(): a wildcard CORS policy and a credentialed
    // request cannot both hold, and AUTH_MODE=off sends the wildcard.
    if (ours && getConfig().auth) xhr.withCredentials = true;
    xhr.setRequestHeader('content-type', 'application/octet-stream');
    xhr.responseType = 'text';

    const onAbort = () => xhr.abort();
    opts.signal?.addEventListener('abort', onAbort, { once: true });
    const cleanup = () => opts.signal?.removeEventListener('abort', onAbort);

    if (opts.onProgress) {
      xhr.upload.onprogress = (e) => opts.onProgress?.(e.loaded);
    }
    xhr.onload = () => {
      cleanup();
      if (xhr.status < 200 || xhr.status >= 300) {
        // The fetch wrapper cannot see this one: parts go over XHR so the
        // upload can report progress. Without this, the single longest-running
        // request in the app is the one place an expiry stays invisible.
        if (xhr.status === 401) noteUnauthorized();
        reject(new UploadApiError(`part PUT failed (${xhr.status})`, xhr.status));
        return;
      }
      // Prefer the header (S3); fall back to the local route's JSON body.
      let etag = xhr.getResponseHeader('ETag') ?? '';
      if (!etag) {
        try {
          etag = (JSON.parse(xhr.responseText) as { etag?: string }).etag ?? '';
        } catch {
          /* no body */
        }
      }
      resolve({ etag: etag.replace(/"/g, '') });
    };
    xhr.onerror = () => {
      cleanup();
      reject(new UploadApiError('network error during part PUT', 0));
    };
    xhr.onabort = () => {
      cleanup();
      reject(new DOMException('aborted', 'AbortError'));
    };
    xhr.send(bytes);
  });
}
